import { Modal, Divider, Tabs } from 'antd';
import { useState, useEffect, useRef } from 'react';
import { useMemoizedFn } from 'ahooks';
import workspaceService from 'services/jobService';
import { useAppDispatch } from 'app/hooks';
import { RevxSpin } from 'components/RevxSpin';
import { ADJUSTMENT_DATE_FORMAT, getFormatDate } from 'components/RevxDate';
import RevxLogoText from 'components/RevxLogoText';
import RevxFlexLayout from 'components/RevxFlexLayout';
import {
  JobAdjustmentViewer,
  JobCancellationStatusEnum,
  JobItem,
  JobAdjustmentProcessStatusEnum,
  JobCancellationViewer,
} from 'domains/workspaceDomain';
import { SiteRoleEnum } from 'domains/roleDomain';
import AdjustOriginal from '../jobAdjustment/components/AdjustOriginal';
import HistoryAdjustOriginal from '../jobAdjustment/components/HistoryAdjustOriginal';
import BuyerAdjustOriginal from '../jobAdjustment/components/BuyerAdjustOriginal';
import HistoryBuyerAdjustOriginal from '../jobAdjustment/components/HistoryBuyerAdjustOriginal';
import JobCancellationSource from './JobCancellationSource';
import { isBuyer, isSupplier, isSourcer } from 'utils/rbac';
import { useUser } from 'hooks';
import { clone, find, includes, get } from 'lodash-es';
import { TabsKeys } from '../jobAdjustment';
import { RevxButton } from 'components/RevxButton';
import './JobAdjustmentModal.less';
import { toast } from 'components/RevxMessage';

interface JobAdjustmentModalProps {
  visible: boolean;
  jobId: number;
  onCancel: () => void;
  onSuccess?: () => void;
  readOnly?: boolean;
  adjustmentId?: number;
}

const JobAdjustmentModal = ({
  visible,
  jobId,
  onCancel,
  onSuccess,
  readOnly,
  adjustmentId,
}: JobAdjustmentModalProps) => {
  const dispatch = useAppDispatch();
  const user = useUser();
  const [loading, setLoading] = useState<boolean>(false);
  const [jobData, setJobData] = useState<JobItem>();
  const [jobAdjustmentData, setJobAdjustmentData] =
    useState<JobAdjustmentViewer[]>();
  const [jobCancellationData, setJobCancellationData] =
    useState<JobCancellationViewer>();
  const [tabsActiveKey, setTabsActiveKey] = useState<TabsKeys>(
    TabsKeys.original
  );
  const [cancelBoxVisible, setCancelBoxVisible] = useState(false);
  const [closeConfirmVisible, setCloseConfirmVisible] = useState(false);
  const [supplierContactIds, setSupplierContactIds] = useState<number[]>([]);
  const adjustRefs = useRef<Record<string, any>>({});

  const loadJobAdjustmentData = useMemoizedFn(async (retryCount = 0) => {
    if (!visible || !jobId) return;

    try {
      setLoading(true);
      const jobItem = await workspaceService.getJobDetail(jobId.toString());
      setJobData(jobItem);

      if (jobItem.cancellationStatus === JobCancellationStatusEnum.NONE) {
        let res: JobAdjustmentViewer[];
        if (adjustmentId) {
          res = await dispatch.rfm.getJobAdjustmentById({
            jobId: jobId.toString(),
            adjustmentId,
          });
        } else {
          res = await dispatch.rfm.jobsAdjustmentsAll(jobId.toString());
        }
        // console.log('Loaded adjustment data:', res);

        // 如果没有获取到数据且重试次数小于3次，则重试
        if ((!res || res.length === 0) && retryCount < 3) {
          console.log(
            `No adjustment data found, retrying... (attempt ${retryCount + 1})`
          );
          setTimeout(() => {
            loadJobAdjustmentData(retryCount + 1);
          }, 1000);
          return;
        }

        setJobAdjustmentData(res);
      } else {
        const cancellationData = await dispatch.rfm.getJobCancellations(
          jobId.toString()
        );
        setJobCancellationData(cancellationData);
      }
    } catch (error) {
      console.error('Failed to load job adjustment data:', error);
      setJobAdjustmentData([]);
    } finally {
      setLoading(false);
    }
  });

  const tabPane = useMemoizedFn(
    (jobAdjustment: JobAdjustmentViewer, idx: number) => {
      if (buyerAdjustShow(jobAdjustment, idx)) {
        return TabsKeys.buyerViewJob;
      }
      if (!jobAdjustment?.promoterRole) {
        return TabsKeys.original;
      } else if (jobAdjustment.promoterRole === SiteRoleEnum.Supplier) {
        return TabsKeys.supplierAdjustment;
      } else if (jobAdjustment.promoterRole === SiteRoleEnum.Sourcer) {
        return TabsKeys.sourcerUpdate;
      }
    }
  );
  useEffect(() => {
    // console.log('JobAdjustmentModal useEffect triggered', { visible, jobId });
    if (visible) {
      // 清空之前的数据，确保重新加载
      setJobData(undefined);
      setJobAdjustmentData(undefined);
      setJobCancellationData(undefined);
      loadJobAdjustmentData();
    }
  }, [visible, jobId, loadJobAdjustmentData]);

  useEffect(() => {
    // 当有数据时，设置默认的 tab
    if (jobAdjustmentData && jobAdjustmentData.length > 0) {
      const lastTabKey = tabPane(
        jobAdjustmentData[jobAdjustmentData.length - 1],
        jobAdjustmentData.length - 1
      );
      setTabsActiveKey(lastTabKey || TabsKeys.original);
    }
  }, [jobAdjustmentData, tabPane]);

  const buyerAdjustShow = useMemoizedFn(
    (item: JobAdjustmentViewer, idx: number) => {
      return Boolean(
        idx !== 0 &&
          [
            JobAdjustmentProcessStatusEnum.AWAITING_ACCEPT,
            JobAdjustmentProcessStatusEnum.ACCEPTED,
          ].includes(item?.processStatus) &&
          (isBuyer(user) || isSupplier(user))
      );
    }
  );

  const handleModalCloseWithoutSuccess = () => {
    setJobData(undefined);
    setJobAdjustmentData(undefined);
    setJobCancellationData(undefined);
    setCancelBoxVisible(false);
    setCloseConfirmVisible(false);
    setTabsActiveKey(TabsKeys.original);
    onCancel();
  };

  const handleModalClose = () => {
    // Check if any adjustment is in EDITING status and has action buttons visible
    // In AdjustOriginal, action buttons are shown when:
    // showSupplierActionBtn || (isOriginalPage && processStatusIsEditing)

    const currentAdjustment = jobAdjustmentData?.find((item) => {
      const key = tabPane(item, jobAdjustmentData.indexOf(item));
      return key === tabsActiveKey;
    });

    const processStatusIsEditing =
      currentAdjustment?.processStatus ===
      JobAdjustmentProcessStatusEnum.EDITING;

    // If in EDITING status and not readOnly, show confirmation
    const inEditingState = !readOnly && processStatusIsEditing;

    console.log('handleModalClose - checking editing state:', {
      readOnly,
      tabsActiveKey,
      currentAdjustment: currentAdjustment
        ? {
            promoterRole: currentAdjustment.promoterRole,
            processStatus: currentAdjustment.processStatus,
            jobAdjustmentId: currentAdjustment.jobAdjustmentId,
          }
        : null,
      processStatusIsEditing,
      inEditingState,
    });

    if (inEditingState) {
      // Show confirmation dialog
      setCloseConfirmVisible(true);
    } else {
      // Directly close
      handleModalCloseWithoutSuccess();
    }
  };

  const handleConfirmClose = async () => {
    try {
      setLoading(true);
      await workspaceService.forceCancelJobAdjustment(jobData?.id || 0);
      // Clear sessionStorage for manual add flag and original items
      const currentAdjustment = jobAdjustmentData?.find((item) => {
        const key = tabPane(item, jobAdjustmentData.indexOf(item));
        return key === tabsActiveKey;
      });
      if (jobId && currentAdjustment?.jobAdjustmentId) {
        sessionStorage.removeItem(
          `revx-original-items-${jobId}-${currentAdjustment.jobAdjustmentId}`
        );
        console.log(
          'Cleared original items from sessionStorage for jobId:',
          jobId,
          ' adjustmentId:',
          currentAdjustment.jobAdjustmentId
        );
        sessionStorage.removeItem(`revx-newly-added-${jobId}`);
      }
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('Failed to cancel job adjustment:', error);
      toast.error('Failed to cancel job adjustment');
    } finally {
      console.log('set loading false');
      setLoading(false);
      handleModalCloseWithoutSuccess();
    }
  };

  const updateData = (data: JobAdjustmentViewer) => {
    const { promoterRole: role } = data;
    if (!jobAdjustmentData) return;
    jobAdjustmentData.forEach(({ promoterRole }, index) => {
      if (role === promoterRole) {
        jobAdjustmentData.splice(index, 1, data);
      }
    });
    if (!jobAdjustmentData?.find(({ promoterRole }) => role === promoterRole)) {
      jobAdjustmentData.push(data);
    }
    setJobAdjustmentData(clone(jobAdjustmentData));
  };

  const jumpTabHandler = async (key: TabsKeys): Promise<void> => {
    // For modal, we might not need tab jumping functionality
    return Promise.resolve();
  };

  const goBackWorkspace = async () => {
    console.log('goBackWorkspace called - about to call onSuccess');
    // 审批或操作完成后，先调用成功回调刷新数据，再关闭模态框
    if (onSuccess) {
      console.log('Calling onSuccess callback');
      await onSuccess(); // 等待刷新完成
      console.log('onSuccess callback completed');
    } else {
      console.log('No onSuccess callback provided');
    }
    console.log('Closing modal');
    handleModalCloseWithoutSuccess();
  };

  const handleJobCancellation = async () => {
    console.log('handleJobCancellation called - reloading job data');
    // 创建 cancellation 后，重新加载数据以显示 cancellation 详情
    await loadJobAdjustmentData();
    // 如果提供了 onSuccess 回调，也调用它以刷新外部列表
    if (onSuccess) {
      console.log('Calling onSuccess callback to refresh workspace');
      await onSuccess();
    }
  };

  const { AWAITING_APPROVAL, AWAITING_ACCEPT } = JobAdjustmentProcessStatusEnum;
  const { Supplier, Sourcer } = SiteRoleEnum;

  const supplierCancelBtn = () => {
    const supplierJobItem = find(jobAdjustmentData, { promoterRole: Supplier });
    const sourcerJobItem = find(jobAdjustmentData, { promoterRole: Sourcer });
    const editingStatuses = [AWAITING_APPROVAL, AWAITING_ACCEPT];
    const isEditing = (item?: JobAdjustmentViewer) =>
      includes(editingStatuses, get(item, 'processStatus'));

    if (
      !readOnly &&
      tabsActiveKey !== TabsKeys.original &&
      ((isSupplier(user) && isEditing(supplierJobItem)) ||
        (isSourcer(user) &&
          (isEditing(sourcerJobItem) || isEditing(supplierJobItem))))
    ) {
      return (
        <div>
          <RevxButton
            brandOrange={true}
            type="primary"
            onClick={() => {
              setCancelBoxVisible(true);
            }}
          >
            Cancel Job Adjustment
          </RevxButton>
        </div>
      );
    }
    return null;
  };

  const handleSubmitJobAdjustment = async () => {
    console.log('handleSubmitJobAdjustment called');
    console.log('tabsActiveKey', tabsActiveKey);
    console.log('jobAdjustmentData', jobAdjustmentData);
    const currentAdjustment = jobAdjustmentData?.find((item) => {
      const key = tabPane(item, jobAdjustmentData.indexOf(item));
      return key === tabsActiveKey;
    });
    console.log('currentAdjustment', currentAdjustment);

    if (currentAdjustment && jobData) {
      // 校验逻辑
      const currentRef = adjustRefs.current[currentAdjustment.jobAdjustmentId];
      if (currentRef && currentRef.validateForm) {
        try {
          await currentRef.validateForm();
        } catch (error) {
          // 校验未通过，直接返回
          console.log('Validation failed, preventing submission');
          toast.error('Validation failed, preventing submission');
          return;
        }
      }

      setLoading(true);
      try {
        await workspaceService.submitJobAdjustmentAll(
          jobData.id,
          supplierContactIds
        );

        sessionStorage.removeItem(`revx-newly-added-${jobId}`);
        // Clear session storage
        if (jobId && currentAdjustment.jobAdjustmentId) {
          sessionStorage.removeItem(
            `revx-original-items-${jobId}-${currentAdjustment.jobAdjustmentId}`
          );

          sessionStorage.removeItem(
            `revx-manual-add-${jobId}-${currentAdjustment.jobAdjustmentId}`
          );
          sessionStorage.removeItem(
            `scroll-to-item-${currentAdjustment.jobAdjustmentId}`
          );
        }

        if (onSuccess) {
          await onSuccess();
        }
      } catch (error) {
        console.error('Failed to submit job adjustment:', error);
      } finally {
        setLoading(false);
        handleModalCloseWithoutSuccess();
      }
    }
  };

  const cancelJobHandler = async () => {
    try {
      setLoading(true);
      await workspaceService.cancelJobAdjustmentAll(jobData?.id || 0, 0);
      if (onSuccess) {
        await onSuccess();
      }
    } catch (error) {
      console.error('Failed to cancel job adjustment:', error);
      toast.error('Failed to cancel job adjustment');
    } finally {
      setLoading(false);
      handleModalCloseWithoutSuccess();
    }
  };

  return (
    <Modal
      title={null}
      visible={visible}
      onCancel={handleModalClose}
      footer={null}
      width={1400}
      centered
      className="job-adjustment-modal"
      wrapClassName="job-adjustment-modal-wrap"
      maskClosable={false}
      destroyOnClose
      bodyStyle={{ padding: 0 }}
    >
      <div className="job-adjustment">
        <RevxSpin spinning={loading}>
          <div className="workspace-header">
            <RevxLogoText
              text="Job Adjustment"
              colors={[
                'gray',
                'gray',
                'gray',
                'blue',
                'red',
                'yellow',
                'black',
                'blue',
                'red',
                'gray',
                'gray',
                'gray',
                'gray',
              ]}
            />
          </div>
          {jobData && (
            <RevxFlexLayout justify="justify-center" className="main-job-info">
              <div>
                <span className="job-info__label">Order Number:</span>
                <span>{jobData.orderId}</span>
              </div>
              <Divider type="vertical" />
              <div>
                <span className="job-info__label">Job Number:</span>
                <span>{jobId}</span>
              </div>
            </RevxFlexLayout>
          )}

          <div className="workspace-content adjustment-main">
            <div className="adjustment-main_tabs">
              {jobData &&
              jobData.cancellationStatus !== JobCancellationStatusEnum.NONE &&
              jobCancellationData ? (
                <div className="workspace-section">
                  <div className="workspace-item">
                    <JobCancellationSource
                      data={jobCancellationData}
                      setJobCancellations={setJobCancellationData}
                      jobData={jobData}
                      jobId={jobId}
                      onSuccess={goBackWorkspace}
                      readOnly={readOnly}
                    />
                  </div>
                </div>
              ) : jobData &&
                jobAdjustmentData &&
                jobAdjustmentData.length > 0 ? (
                jobAdjustmentData.length === 1 && !isBuyer(user) ? (
                  <div className="workspace-section">
                    <div className="workspace-item">
                      {readOnly ? (
                        <HistoryAdjustOriginal
                          data={jobAdjustmentData[0]}
                          jobId={jobId}
                          goBackWorkspace={goBackWorkspace}
                          updateJobAdjustmentData={updateData}
                          jumpTab={jumpTabHandler}
                          refreshJobAdjustmentData={loadJobAdjustmentData}
                          jobData={jobData}
                        />
                      ) : (
                        <AdjustOriginal
                          ref={(r) => {
                            if (r)
                              adjustRefs.current[
                                jobAdjustmentData[0].jobAdjustmentId
                              ] = r;
                          }}
                          data={jobAdjustmentData[0]}
                          jobId={jobId}
                          goBackWorkspace={goBackWorkspace}
                          updateJobAdjustmentData={updateData}
                          jumpTab={jumpTabHandler}
                          refreshJobAdjustmentData={loadJobAdjustmentData}
                          jobData={jobData}
                          onSupplierContactsChange={setSupplierContactIds}
                        />
                      )}
                    </div>
                  </div>
                ) : (
                  <>
                    {cancelBoxVisible && (
                      <div
                        className="cancel-overlay"
                        style={{
                          position: 'fixed',
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          backgroundColor: 'rgba(0, 0, 0, 0.45)',
                          zIndex: 1000,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <div
                          style={{
                            backgroundColor: '#ffffff',
                            padding: '24px',
                            borderRadius: '6px',
                            boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
                            maxWidth: '416px',
                            textAlign: 'center',
                          }}
                        >
                          <div
                            style={{
                              marginBottom: '16px',
                              fontSize: '16px',
                              fontWeight: 400,
                              color: '#000000d9',
                            }}
                          >
                            Are you sure you want to cancel this job adjustment?
                          </div>
                          <RevxFlexLayout justify="justify-center">
                            <RevxButton
                              type="primary"
                              brandGreen={true}
                              style={{ marginRight: '8px' }}
                              onClick={async () => {
                                await cancelJobHandler();
                              }}
                            >
                              Yes, cancel
                            </RevxButton>
                            <RevxButton
                              onClick={() => {
                                setCancelBoxVisible(false);
                              }}
                            >
                              No, don't cancel
                            </RevxButton>
                          </RevxFlexLayout>
                        </div>
                      </div>
                    )}
                    <Tabs
                      activeKey={tabsActiveKey}
                      tabBarExtraContent={{ right: supplierCancelBtn() }}
                      onChange={(v: string) => {
                        setTabsActiveKey(v as TabsKeys);
                      }}
                    >
                      {jobAdjustmentData?.map((item, idx) => {
                        return (
                          <Tabs.TabPane
                            tab={`${tabPane(item, idx)} ${
                              item.promoterRole
                                ? getFormatDate({
                                    dateStr: item.updateAt || '',
                                    formatStr: ADJUSTMENT_DATE_FORMAT,
                                    isLocalTime: true,
                                  })
                                : ''
                            }`}
                            key={tabPane(item, idx)}
                          >
                            <div className="workspace-section">
                              <div className="workspace-item">
                                {buyerAdjustShow(item, idx) ? (
                                  readOnly ? (
                                    <HistoryBuyerAdjustOriginal
                                      data={item}
                                      originalData={jobAdjustmentData[0]}
                                      jobData={jobData}
                                    />
                                  ) : (
                                    <BuyerAdjustOriginal
                                      data={item}
                                      updateJobAdjustmentData={updateData}
                                      setLoading={setLoading}
                                      originalData={jobAdjustmentData[0]}
                                      jobData={jobData}
                                      goBackWorkspace={goBackWorkspace}
                                      onJobCancellation={handleJobCancellation}
                                    />
                                  )
                                ) : readOnly ? (
                                  <HistoryAdjustOriginal
                                    originalData={jobAdjustmentData[0]}
                                    data={item}
                                    jobId={jobId}
                                    goBackWorkspace={goBackWorkspace}
                                    updateJobAdjustmentData={updateData}
                                    jumpTab={jumpTabHandler}
                                    refreshJobAdjustmentData={
                                      loadJobAdjustmentData
                                    }
                                    jobData={jobData}
                                  />
                                ) : (
                                  <AdjustOriginal
                                    ref={(r) => {
                                      if (r)
                                        adjustRefs.current[
                                          item.jobAdjustmentId
                                        ] = r;
                                    }}
                                    originalData={jobAdjustmentData[0]}
                                    data={item}
                                    jobId={jobId}
                                    goBackWorkspace={goBackWorkspace}
                                    updateJobAdjustmentData={updateData}
                                    jumpTab={jumpTabHandler}
                                    refreshJobAdjustmentData={
                                      loadJobAdjustmentData
                                    }
                                    jobData={jobData}
                                    onSupplierContactsChange={
                                      setSupplierContactIds
                                    }
                                  />
                                )}
                              </div>
                            </div>
                          </Tabs.TabPane>
                        );
                      })}
                    </Tabs>
                  </>
                )
              ) : (
                <div style={{ padding: '20px', textAlign: 'center' }}>
                  {loading ? 'Loading...' : 'No data available'}
                </div>
              )}
            </div>
          </div>
        </RevxSpin>
      </div>
      {closeConfirmVisible && (
        <div
          className="close-confirm-overlay"
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.45)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={(e) => {
            // Prevent click from propagating to elements below
            e.stopPropagation();
          }}
        >
          <div
            style={{
              backgroundColor: '#ffffff',
              padding: '24px',
              borderRadius: '6px',
              boxShadow: '0 4px 12px 0 rgba(0, 0, 0, 0.15)',
              maxWidth: '416px',
              textAlign: 'center',
              position: 'relative',
              zIndex: 1002,
            }}
            onClick={(e) => {
              // Prevent click from closing the dialog when clicking inside
              e.stopPropagation();
            }}
          >
            <div
              style={{
                marginBottom: '24px',
                fontSize: '16px',
                fontWeight: 400,
                color: '#000000d9',
              }}
            >
              Do you want to submit or cancel the current job adjustment?
            </div>
            <RevxFlexLayout
              justify="justify-center"
              style={{ marginBottom: '24px' }}
            >
              <RevxButton
                type="primary"
                brandGreen={true}
                style={{ marginRight: '16px' }}
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleSubmitJobAdjustment();
                }}
              >
                Submit
              </RevxButton>
              <RevxButton
                brandOrange={true}
                type="primary"
                onClick={async (e) => {
                  e.stopPropagation();
                  await handleConfirmClose();
                }}
              >
                Cancel
              </RevxButton>
            </RevxFlexLayout>
            <RevxFlexLayout justify="justify-center">
              <RevxButton
                onClick={(e) => {
                  e.stopPropagation();
                  setCloseConfirmVisible(false);
                }}
              >
                Back to Edit
              </RevxButton>
            </RevxFlexLayout>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default JobAdjustmentModal;
